<script lang="ts" setup>
	import type { BlogPostContext } from '~/server/type'
	import StringMethods from '~/utils/character'

	const { id } = useRoute().params
	const { back } = useRouter()
	const { fullPath } = useRoute()
	const postId = String(id).split('-')[0]
	const { toCapitalize, stripHTMLTags, extractHeadingsWithIds } = StringMethods()
	const postTitleSlug = computed(() => {
		const slug = String(id).split('-')
		slug.splice(0, 1)
		const title = slug.join(' ')
		return toCapitalize(title)
	})
	const fullUrl = computed(() => `${import.meta.env.VITE_BASE_URL}${fullPath}`)

	const { formatDate } = useCustomDate()
	const { getAllGuides } = useProductGuideApi()
	const data = ref<PaginationContext<BlogPostContext> | null>(null)
	const isLoading = shallowRef(true)
	const updating = shallowRef(false)
	const post = ref<BlogPostContext | null>(null)
	const title = computed(() => toCapitalize(post.value?.title ?? postTitleSlug.value).replaceAll('-', ' '))
	const crumbs = computed(() => {
		const list = useRoute().fullPath.split('/')
		const stringSubCat = String(post.value?.categories?.[0]?.name ?? '')
		if (stringSubCat) {
			// Concatenate the category name to the URL
			list.splice(2, 0, String(stringSubCat))
		}
		// Remove the last element (the post title) from the array
		list.splice(-1, 1, title.value)

		return list.map((links, i) => {
			const base = links === '/' || links === ''
			return {
				label: base ? 'Home' : toCapitalize(links).replaceAll('-', ' '),
				to: base ? '/' : i < 2 ? '/product-guide' : `/product-guide/${id}`
			}
		})
	})
	const postAuthor = computed(() => {
		if (post.value?.author && Array.isArray(post.value.author)) {
			return post.value?.author[0]
		}
		return null
	})
	const sideLinks = computed(
		() => data.value?.items?.map(p => ({ title: p.title, to: `${p.categories[0].id}-${p.slug}`, id: p.id })) ?? []
	)

	const seoTags = computed<Record<string, unknown>>(() => ({
		title: `${title.value || postTitleSlug.value}`,
		description: stripHTMLTags(post.value?.excerpt ?? ''),
		ogTitle: `${title.value || postTitleSlug.value}`,
		ogDescription: stripHTMLTags(post.value?.excerpt ?? ''),
		ogImage: post.value?.media?.src ?? '',
		ogUrl: fullUrl.value,
		twitterTitle: `${title.value || postTitleSlug.value}`,
		twitterDescription: stripHTMLTags(post.value?.excerpt ?? ''),
		twitterImage: post.value?.media?.src ?? '',
		twitterCard: 'summary_large_image',
		twitterSite: '@MyCoverGenius',
		twitterCreator: '@MyCoverGenius',
		articleAuthor: [postAuthor.value?.name ?? ''],
		articlePublishedTime: post.value?.date ? formatDate(post.value?.date ?? '') : '',
		articleTag: post.value?.tags ?? [],
		articleSection: post.value?.categories?.[0] ?? '',
		author: postAuthor.value?.name ?? '',
		ogType: 'article',
		ogLocale: 'en_US'
	}))
	const getNextPost = async (id: number) => {
		const newPost = data.value?.items?.find(p => p.id == id)
		updating.value = true
		if (newPost) {
			post.value = newPost
			await useTimeouts()
			updating.value = false
		}
		return post.value
	}
	useServerSeoMeta(seoTags.value)
	useSeoMeta(seoTags.value)
	const { urlQuery } = useUtils()
	onMounted(async () => {
		const response = await resolve(() => getAllGuides(postId), isLoading)
		if (response?.items?.length) {
			data.value = response
			const postResponse =
				response.items.find(p => String(p.id) == String(urlQuery.value?.q)) || response.items.at(0)
			if (postResponse) {
				post.value = postResponse
			}
		}
	})
</script>

<template>
	<div>
		<loader-article-loader v-if="isLoading || updating" />
		<header
			class="md:min-h-[420px] min-h-[300px] sm:min-h-[400px] relative mt-0 bg-base-800 dark:bg-base-900 py-12 xs:pt-16"
		>
			<ui-pad>
				<ui-back-btn class="bg-white mb-8" label="back" @click="back" />

				<client-only>
					<u-breadcrumb
						:links="crumbs"
						:ui="{
							li: 'hidden md:flex items-center gap-x-1.5 text-[#FAFBFD] dark:text-gray-100 text-sm leading-6 min-w-0',
							active: '',
							inactive: 'hover:text-success-300'
						}"
						class="pb-8"
					/>
					<ui-h1
						class="text-left"
						:title="title"
						:paragraph="post?.excerpt ?? ''"
						:ui="{
							btn: { base: 'hidden' },
							paragraphClass: {
								maxWidth: 'max-w-[879px]',
								base: 'text-pretty pt-[16px]'
							},
							wrapperClass: {
								alignItems: '',
								maxWidth: '',
								default: 'md:pt-5'
							},
							hClass: {
								wrapper: {
									size: 'text-[2em] xs:text-[2.6em] text-[inherit] capitalize lg:text-[4em] lg:leading-[70px] xs:leading-[50px] leading-[40px] text-pretty'
								}
							}
						}"
					/>
				</client-only>
			</ui-pad>
		</header>

		<ui-pad :ui="{ margin: 'md:mt-10', paddingInline: 'md:px-10 px-0' }">
			<article class="md:flex items-stretch justify-between">
				<div class="h-full sticky top-32 left-0 md:pr-3 lg:block hidden max-w-[350px]">
					<div class="pt-8 invisible-scrollbar max-h-screen space-y-12 overflow-y-auto lg:pb-24 flex-1">
						<molecules-article-author
							:name="postAuthor?.name ?? ''"
							:content="post?.content ?? ''"
							:created-date="post?.date ?? ''"
							:img-src="postAuthor?.avatar_urls?.[48] ?? ''"
						>
							<template #time>Last Updated {{ formatDate(post?.date ?? '', {}) }}</template>
						</molecules-article-author>

						<client-only>
							<ul class="space-y-2">
								<li
									v-for="(item, index) in sideLinks"
									:key="`route-${index}`"
									class="border-l-[6px] border-transparent cursor-pointer max-w-[300px] transition py-1 rounded-md pl-5"
									:class="[item.id === post?.id ? 'active' : '!text-gray-700']"
									@click="() => getNextPost(item.id)"
								>
									<span
										:class="[item.id === post?.id ? '!text-main' : '!text-gray-700']"
										v-html="item.title"
									/>
								</li>
							</ul>
						</client-only>
					</div>
				</div>

				<div
					class="flex-1 flex flex-col w-full px-5 pt-5 md:pt-0 mx-auto lg:max-w-[850px] border-l border-gray-300"
				>
					<molecules-article-author
						:name="postAuthor?.name ?? ''"
						:content="post?.content ?? ''"
						:created-date="post?.date ?? ''"
						:img-src="postAuthor?.avatar_urls?.[48] ?? ''"
						class="lg:hidden mb-5"
					/>
					<div class="w-full">
						<div class="pb-5 pt-3 w-full lg:px-5 px-3 border-gray-200">
							<section class="wp-content" ref="article-wrapper" v-html="post?.content ?? ''" />
						</div>
						<div class="pt-[64px]">
							<molecules-related-guides :current-id="post?.id" :posts="data?.items || []" />
						</div>
					</div>
				</div>
			</article>
		</ui-pad>
	</div>
</template>

<style scoped>
	.wp-content :deep(h2),
	.wp-content :deep(h3),
	.wp-content :deep(h4) {
		@apply font-medium text-gray-700;
	}

	.wp-content :deep(h1),
	.wp-content :deep(h2),
	.wp-content :deep(h3),
	.wp-content :deep(h4),
	.wp-content :deep(h5),
	.wp-content :deep(h6) {
		@apply pt-8 pb-2;
	}

	.wp-content {
		@apply sm:text-lg text-base sm:leading-[32px] text-gray-700 font-normal;
	}

	.wp-content :deep(.wp-block-separator) {
		@apply my-5;
	}

	/*
	WORDPRESS CUSTOM COLOR PANEL FOR TEXT
	*/
	.mark-down-content:deep(.has-contrast-background-color) {
		@apply bg-gray-950 text-gray-50;
	}

	.mark-down-content:deep(.has-accent-1-background-color) {
		@apply bg-amber-400 text-gray-50;
	}

	.mark-down-content:deep(.has-accent-2-background-color) {
		@apply bg-pink-400 text-gray-50;
	}

	.mark-down-content:deep(.has-accent-3-background-color) {
		@apply bg-purple-500 text-white;
	}
	.mark-down-content:deep(.has-accent-4-background-color) {
		@apply bg-gray-500 text-gray-50;
	}

	.mark-down-content:deep(.has-accent-6-background-color) {
		@apply bg-gray-400 text-gray-50;
	}
	/*
	WORDPRESS CUSTOM COLOR PANEL FOR TEXT
	*/
	.mark-down-content:deep(.has-contrast-color) {
		@apply text-gray-950;
	}
	.mark-down-content:deep(.has-accent-1-color) {
		@apply text-amber-400;
	}
	.mark-down-content:deep(.has-accent-2-color) {
		@apply text-pink-400;
	}

	.mark-down-content:deep(.has-accent-3-color) {
		@apply text-purple-500;
	}
	.mark-down-content:deep(.has-accent-4-color) {
		@apply text-gray-500;
	}
	.mark-down-content:deep(.has-accent-6-color) {
		@apply text-gray-400;
	}
	/*===========>*/

	.wp-content :deep(.wp-block-button__link) {
		@apply md:px-5 md:py-3 md:text-base px-1.5
		sm:min-h-[42px]
		min-h-[40px]
		rounded-[300px]
		xs:text-sm
		text-xs
		transition
		font-medium
		inline-flex
		items-center
		no-underline
		text-white
		my-5
		mx-auto
		gap-2;
	}

	.wp-content :deep(p) {
		@apply mt-5 mb-3;
	}

	.wp-content :deep(.wp-block-image) {
		@apply mt-5;
	}

	.wp-content :deep(h1) {
		@apply font-[700] text-blue-950 sm:text-[32px] sm:leading-[44.8px] text-2xl;
	}

	.wp-content :deep(h2) {
		@apply sm:text-[28px] sm:leading-[39.2px] text-xl;
	}

	.wp-content :deep(h3) {
		@apply sm:text-2xl sm:leading-[33.6px] text-lg;
	}

	.wp-content :deep(h4) {
		@apply sm:text-xl sm:leading-[28px] text-base font-medium;
	}

	.wp-content :deep(h5) {
		@apply text-base leading-[22.4px] font-medium;
	}

	.wp-content :deep(ol),
	.wp-content :deep(ul),
	.wp-content :deep(menu) {
		list-style: outside;
		text-wrap: pretty;
	}
	.wp-content :deep(ol) {
		@apply list-decimal;
	}
	.wp-content :deep(li .wp-block-list) {
		@apply ml-5 mb-3;
	}
	.wp-content :deep(li) {
		@apply mb-3;
	}

	@media (min-width: 640px) {
		.wp-content :deep(ol),
		.wp-content :deep(ul),
		.wp-content :deep(menu) {
			list-style-position: inside;
		}
	}

	.wp-content :deep(a) {
		@apply text-base-primary underline;
	}

	.active {
		@apply font-semibold text-base border-main;
	}
</style>
